import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { NextPrevContext } from "../../context/nextContext";
import { BREAK_TYPE, measureTextHeight, paginateTextBySize } from "../../functions/splitText";
import { useTimer } from "../../hooks/utilities/useTimer";
import styles from "../slideBlocks/TextBlock.module.scss";
import { TextSlide, TextSlideProps, TextSlideRef } from "./TextSlide";

export type NewsSlideProps = Omit<TextSlideProps, "seconds"> & {
  onCompleted: () => void;
  onBack?: () => void;
};

export const NewsSlide: FC<NewsSlideProps> = ({ title, text, ...props }) => {
  const [index, setIndex] = useState<number>(0);
  const [contentArray, setContentArray] = useState<{ content: string; height: number }[]>([]);
  const [textHeight, setTextHeight] = useState<number>(0);

  const textSlideRef = useRef<TextSlideRef>(null);
  const PrevNextContext = useContext(NextPrevContext);

  const nextSlide = useCallback(() => {
    if (index < contentArray.length - 1) {
      setIndex((index) => index + 1);
      if (PrevNextContext.autoGoNext) {
        resetTimer();
      }
    } else {
      props.onCompleted();
    }
  }, [index, contentArray]);

  const prevSlide = useCallback(() => {
    if (index > 0) {
      setIndex((index) => index - 1);
    } else {
      props.onBack?.();
    }
  }, [index]);

  const calculatedDuration = useMemo(() => {
    if(contentArray[index]?.height == null) {
      return props.duration;
    }
    return ((contentArray[index]?.height ?? 0) / textHeight) * props.duration;
  }, [contentArray, index, props.duration, textHeight]);

  const { seconds, resetAndStartTimer: resetTimer } = useTimer(calculatedDuration, nextSlide);

  useEffect(() => {
    PrevNextContext.setNext(() => {
      console.log("next NewsSlide");
      nextSlide();
    });
  }, [nextSlide]);

  useEffect(() => {
    PrevNextContext.setPrev(() => {
      console.log("prev NewsSlide");
      prevSlide();
    });
  }, [prevSlide]);

  function setHeight() {
    // Check if fonts are loaded
    const width = 1250;
    const parent = textSlideRef.current?.parent;
    // get the height of the parent without padding
    const parentComputedStyle = window.getComputedStyle(parent ?? document.body);
    const parentHeight = (parent?.clientHeight ?? 20) - parseFloat(parentComputedStyle.paddingTop) - parseFloat(parentComputedStyle.paddingBottom);
    const titleHeight = measureTextHeight(title, width + "px", {}, styles.title);
    
    const height = parentHeight - titleHeight; // measureTextHeight(title ?? '', "1000px", {},styles.title)
    setTextHeight(height);
    const array = paginateTextBySize(width ?? 1000, height, { color: "white" }, styles.content, BREAK_TYPE.SENTENCE)(text);
    if (PrevNextContext.autoGoNext) {
      resetTimer();
    }
    setIndex(0);
    setContentArray(array);
  }

  useEffect(() => {
    setHeight();
    document.fonts.ready.then(() => {
      setHeight();
    });
  }, [text]);

  return (
    <div style={{ position: "relative" }}>
      <div style={{ position: "absolute", left: 1755, top: 960, zIndex: 1 }} className={"small-content-text"}>
        {index + 1}/{contentArray.length}
      </div>      
      <TextSlide ref={textSlideRef} {...{ ...props, title, text }} seconds={seconds} duration={calculatedDuration} text={contentArray[index]?.content} />
    </div>
  );
};
